/**
 * Run a background task only while the page is visible and never overlap it.
 * Returning to the tab triggers one immediate refresh so the UI catches up
 * without spending network or CPU while the seller is away.
 */
export function createVisiblePoller({
  task,
  intervalMs,
  documentRef = document,
  timerApi = window,
}) {
  if (typeof task !== "function") throw new TypeError("task must be a function");
  if (!Number.isFinite(intervalMs) || intervalMs <= 0) {
    throw new TypeError("intervalMs must be a positive number");
  }

  let stopped = false;
  let inFlight = false;

  const run = async () => {
    if (stopped || documentRef.hidden || inFlight) return false;
    inFlight = true;
    try {
      await task();
      return true;
    } finally {
      inFlight = false;
    }
  };

  const trigger = () => { void run().catch(() => {}); };
  const onVisibility = () => {
    if (!documentRef.hidden) trigger();
  };
  const intervalId = timerApi.setInterval(trigger, intervalMs);
  documentRef.addEventListener("visibilitychange", onVisibility);

  return {
    run,
    stop() {
      if (stopped) return;
      stopped = true;
      timerApi.clearInterval(intervalId);
      documentRef.removeEventListener("visibilitychange", onVisibility);
    },
  };
}
